import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { commentServices, instagramService } from '@/services/instagramService'
import type { CommentChannel, InstagramCommentAutomation } from '@/services/instagramService'
import { messengerService } from '@/services/messengerService'
import { queryKeys } from '../keys'

export function useInstagramStatusQuery() {
  return useQuery({
    queryKey: queryKeys.meta.instagramStatus(),
    queryFn: () => instagramService.getStatus(),
  })
}

export function useMessengerStatusQuery() {
  return useQuery({
    queryKey: queryKeys.meta.messengerStatus(),
    queryFn: () => messengerService.getStatus(),
  })
}

export function useCommentMediaQuery(channel: CommentChannel) {
  return useQuery({
    queryKey: queryKeys.meta.commentMedia(channel),
    queryFn: () => commentServices[channel].listMedia(),
  })
}

export function useCommentsQuery(channel: CommentChannel, mediaId: string | null) {
  return useQuery({
    queryKey: queryKeys.meta.comments(channel, mediaId ?? ''),
    queryFn: () => commentServices[channel].listComments(mediaId as string),
    enabled: !!mediaId,
  })
}

export function useCommentAutomationQuery(channel: CommentChannel) {
  return useQuery({
    queryKey: queryKeys.meta.commentAutomation(channel),
    queryFn: () => commentServices[channel].getCommentAutomation(),
  })
}

export function useInstagramInsightsQuery(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.meta.instagramInsights(),
    queryFn: () => instagramService.getInsights(),
    enabled,
  })
}

export function useSaveCommentAutomation(channel: CommentChannel) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (rule: InstagramCommentAutomation) => commentServices[channel].saveCommentAutomation(rule),
    onSuccess: (data) => queryClient.setQueryData(queryKeys.meta.commentAutomation(channel), data),
  })
}

type CommentAction =
  | { kind: 'reply'; commentId: string; message: string }
  | { kind: 'private-reply'; commentId: string; message: string }
  | { kind: 'hide'; commentId: string; hidden: boolean }

/** Reply, DM or hide a comment, then reload that post's comments. */
export function useCommentAction(channel: CommentChannel, mediaId: string | null) {
  const queryClient = useQueryClient()
  const service = commentServices[channel]
  return useMutation({
    mutationFn: (action: CommentAction) => {
      if (action.kind === 'reply') return service.replyToComment(action.commentId, action.message)
      if (action.kind === 'private-reply') return service.sendPrivateReply(action.commentId, action.message)
      return service.setCommentHidden(action.commentId, action.hidden)
    },
    onSuccess: () => {
      if (mediaId) queryClient.invalidateQueries({ queryKey: queryKeys.meta.comments(channel, mediaId) })
    },
  })
}
